import BackendService from 'BackendService';
import { useEffect, useState } from 'react';
import {
  ActivityLogExtendedInfo,
  activityLogModelToContentModel,
  ActivityLog as ActivityLogType,
} from '../../models/ActivityLog';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { fetchExtendedInfo } from '../../utils/ExtendedInfoService';
import { extractContentDiffPayload, ContentDiffPayload } from './ContentDiffViewer';
import { applyDiffReverse } from './contentVersionViewerUtils';
import { isContentDiffEvent } from './activityLogEntryUtils';

export interface ContentVersionData {
  activityLog: ActivityLogType | null;
  currentContent: ContentManagementInterface | null;
  fullContent: {
    originalContent: ContentManagementInterface;
    newContent: ContentManagementInterface;
  } | null;
  previousContent: ContentManagementInterface | null;
  diff: ContentDiffPayload | null;
  currentUser: string | null;
}

export interface UseContentVersionDataResult extends ContentVersionData {
  loading: boolean;
  loadingMessage: string | null;
  error: string | null;
}

export function useContentVersionData(
  activityLogId: string | undefined,
): UseContentVersionDataResult {
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activityLog, setActivityLog] = useState<ActivityLogType | null>(null);
  const [currentContent, setCurrentContent] = useState<ContentManagementInterface | null>(null);
  const [fullContent, setFullContent] = useState<ContentVersionData['fullContent']>(null);
  const [previousContent, setPreviousContent] = useState<ContentManagementInterface | null>(null);
  const [diff, setDiff] = useState<ContentDiffPayload | null>(null);
  const [currentUser, setCurrentUser] = useState<string | null>(null);

  useEffect(() => {
    if (!activityLogId) {
      setError('Activity log ID is required');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const backendService = new BackendService();

        setLoadingMessage('Loading user information...');
        try {
          const user = await backendService.getCurrentUser();
          setCurrentUser(user?.id || user?.email || null);
        } catch (err) {
          console.warn('ContentVersionViewer: Could not load current user:', err);
          setCurrentUser(null);
        }

        const activityLogResponse = await backendService.getActivityLogEntry(activityLogId);
        setActivityLog(activityLogResponse);

        if (!isContentDiffEvent(activityLogResponse.eventType)) {
          setError('This activity log entry is not a content management event');
          setLoading(false);
          return;
        }

        let extendedInfo: ActivityLogExtendedInfo | undefined = activityLogResponse.extendedInfo;
        if (!extendedInfo && activityLogResponse.extendedInfoPath) {
          setLoadingMessage('Loading extended info...');
          extendedInfo =
            (await fetchExtendedInfo(activityLogResponse.extendedInfoPath, backendService)) ||
            undefined;
        }

        if (activityLogResponse.originalContentPath && activityLogResponse.newContentPath) {
          setLoadingMessage('Loading content for comparison...');
          const originalContent = (await fetchExtendedInfo(
            activityLogResponse.originalContentPath,
            backendService,
          )) as ContentManagementInterface;
          const newContent = (await fetchExtendedInfo(
            activityLogResponse.newContentPath,
            backendService,
          )) as ContentManagementInterface;
          if (originalContent && newContent) {
            setFullContent({ originalContent, newContent });
          }
        }

        const diffPayload = extractContentDiffPayload(extendedInfo);
        if (!diffPayload) {
          setError('No diff data available for this activity log entry');
          setLoading(false);
          return;
        }
        setDiff(diffPayload);

        const modelType = activityLogResponse.associatedModelType;
        const modelId = activityLogResponse.associatedModelExternalId;

        const currentContentData = await backendService.getContentManagementWithToken(
          activityLogModelToContentModel(modelType),
          modelId,
          false,
        );

        if (currentContentData) {
          const parsedCurrentContent = JSON.parse(currentContentData);
          setCurrentContent(parsedCurrentContent);
          setPreviousContent(applyDiffReverse(parsedCurrentContent, diffPayload));
        } else {
          setError('Could not load current content');
        }
      } catch (err) {
        console.error('Error loading content version data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [activityLogId]);

  return {
    loading,
    loadingMessage,
    error,
    activityLog,
    currentContent,
    fullContent,
    previousContent,
    diff,
    currentUser,
  };
}
